import { ReactNode, FC, Fragment, useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { push } from 'connected-react-router';
// Models
import { ICurrentAccount } from 'app/models/Account';
import IUser, { IMyUser } from 'app/models/User';
import IContact from 'app/models/Contact';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { getTeams } from 'app/store/Teams/Teams.async';
import { createInsuranceCase } from 'app/store/Cases/Cases.async';
import { selectLoading } from 'app/store/Cases/Cases.selectors';
// Api query
import casesApi, { useLazyGetSearchCasesQuery } from 'app/store/Cases/Cases.api';
// Selector
import { selectMyUser } from 'app/store/Users/Users.selectors';
import { selectCurrentAccount, selectHcpcsCodesEnabled } from 'app/store/Accounts/Accounts.selectors';
// Mui
import { SxProps, Grid, Stepper, Step, StepLabel, Box, Theme } from '@mui/material';
// Icons
import {
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
// Components
import Dialog from 'app/components/Dialog';
import { Button } from 'app/components/Mui';
// Hooks
import useToggle from 'app/hooks/useToggle';
// Utilities
import { convertDateToString, convertTimeToString } from 'app/utilities/DateTime';
// Check dialog
import CaseCheckDialog from '../CaseCheckDialog';
// 
import GeneralStepContent from './GeneralStepContent';
import ClaimantStepContent from './ClaimantStepContent';
import MedicareStepContent from './MedicareStepContent';
import NotificationsStepContent from './NotificationsStepContent';
import ReviewCreateStepContent from './ReviewCreateStepContent';
import DetailsStepContent from './DetailsStepContent';
import ContactsStepContent from './ContactsStepContent';

interface IFormData {
  name: string;
  teamId: string | number;
  clientAccountId?: string | number;
  claimant: {
    name: string;
    dateOfBirth: Dayjs | null;
  },
  settings: {
    aiChatEnabled: boolean;
    medicareOverchargeThreshold: number;
  };
  eventDate: Dayjs | null,
  eventTime: Dayjs | null;
  claimNumber: string;
  notificationPreferences: {
    recipientIds: IUser[];
  };
  type: string;
  summary: string;
  expectedOutcome: string;
  contacts: {
    contact: IContact | null;
    role: string;
  }[];
};

type Props = {
  open: boolean;
  onClose: () => void;
}

const CaseCreateFormDialog:FC<Props> = ({
  // Props
  open, onClose
}) => {
  const { t } = useTranslation();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const myUser = useAppSelector(selectMyUser) as IMyUser;
  const currentAccount = useAppSelector(selectCurrentAccount) as ICurrentAccount;
  const hcpcsCodesEnabled = useAppSelector(selectHcpcsCodesEnabled);
  const loading = useAppSelector(selectLoading);
  // Query
  const [ getSearchCases, { data:searchCasesData, isLoading } ] = useLazyGetSearchCasesQuery();

  const steps = [
    t('labels.general'),
    t('labels.claimant'),
    t('labels.contacts'),
    t('labels.details'),
    hcpcsCodesEnabled ? t('labels.medicare') : '',
    t('labels.notifications'),
    t('labels.reviewAndCreate')
  ].filter(s => s);

  const stepFields: (string[] | null)[] = [
    ['name', 'eventDate', 'eventTime', 'type', 'teamId', 'clientAccountId'],
    ['claimant', 'claimNumber'],
    ['contacts'],
    ['summary', 'expectedOutcome'],
    ['settings'],
    ['notificationPreferences.recipientIds'],
    []
  ].filter(s => s !== null);

  const methods = useForm<IFormData>({
    defaultValues: {
      name: '',
      teamId: '',
      clientAccountId: '',
      settings: {
        aiChatEnabled: true,
        medicareOverchargeThreshold: currentAccount.settings.medicareOverchargeThreshold
      },
      claimant: {
        name: '',
        dateOfBirth: null
      },
      eventDate: null,
      eventTime: null,
      claimNumber: '',
      notificationPreferences: {
        recipientIds: [myUser]
      },
      contacts: []
    }
  });

  const { trigger, handleSubmit, formState: { errors } } = methods;

  const [ activeStep, setActiveStep ] = useState(0);
  const [ visitedSteps, setVisitedSteps ] = useState<number[]>([0]);
  const [ isSummaryStepVisited, setIsSummaryStepVisited ] = useState(false);
  const [ caseFormData, setCaseFormData ] = useState<IFormData | null>(null);

  const { open:openCheckDialog, toggle:toggleCheckDialog } = useToggle();

  useEffect(() => {
    dispatch(getTeams({}));

    return () => {
      dispatch(casesApi.util.resetApiState());
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if ( searchCasesData ){
      if ( Object.keys(searchCasesData).length ){
        toggleCheckDialog();
      } else {
        asyncCreateInsuranceCase();
      }
    }
    // eslint-disable-next-line
  }, [searchCasesData]);

  const onSubmit = handleSubmit((data:IFormData) => {
    const {
      teamId, clientAccountId, settings, name,
      claimant, eventDate, eventTime, claimNumber, notificationPreferences,
      type, summary, expectedOutcome, contacts
    } = data;
    const caseData:any = {
      name,
      eventDate: eventDate ? convertDateToString(eventDate) : null,
      claimant: {
        name: claimant.name
      },
      settings: {
        aiChatEnabled: settings.aiChatEnabled
      }
    };
    if ( claimant.dateOfBirth ) caseData['claimant']['dateOfBirth'] = convertDateToString(claimant.dateOfBirth);
    if ( teamId ) caseData['teamId'] = Number(teamId);
    if ( clientAccountId ) caseData['clientAccountId'] = Number(clientAccountId);
    if ( eventTime ) caseData['eventTime'] = convertTimeToString(eventTime);
    if ( claimNumber ) caseData['claimNumber'] = claimNumber;
    if ( notificationPreferences ){
      const { recipientIds } = notificationPreferences;
      if ( recipientIds && recipientIds.length ){
        caseData['notificationPreferences'] = {
          recipientIds: recipientIds.map((recipient:IUser) => recipient.id)
        };
      }
    }
    if ( type ) caseData['type'] = type;
    if ( summary ) caseData['summary'] = summary;
    if ( expectedOutcome ) caseData['expectedOutcome'] = expectedOutcome;

    if ( hcpcsCodesEnabled ){
      caseData['settings']['medicareOverchargeThreshold'] = Number(settings.medicareOverchargeThreshold);
    }

    if ( contacts && contacts.length ) {
      caseData['contacts'] = contacts.map(({ contact, role }) => ({
        contactId: contact?.id,
        role
      }));
    }

    if ( caseData.claimant.dateOfBirth ){
      setCaseFormData(caseData);
      getSearchCases({
        params: {
          'claimant.name': caseData.claimant.name,
          'claimant.dateOfBirth': caseData.claimant.dateOfBirth,
        },
        caseId: undefined
      });
    } else {
      asyncCreateInsuranceCase(caseData);
    }
  });

  const asyncCreateInsuranceCase = async (data?:any) => {
    dispatch(casesApi.util.resetApiState());

    try {
      const insuranceCase = await dispatch(createInsuranceCase(data || caseFormData)).unwrap();
      dispatch(push(`/admin/cases/${insuranceCase.id}`));
      // onClose();
    } catch(error){}
  }

  const handleStepClick = (step:number) => async () => {
    const isSummaryStep = step === steps.length - 1;
    setActiveStep(step);
    if ( isSummaryStepVisited ){
      await trigger(stepFields[activeStep] as (keyof IFormData)[]);
    } else {
      if ( isSummaryStep ){
        if ( !isSummaryStepVisited ) setIsSummaryStepVisited(true);
        await Promise.all(stepFields.slice(0, steps.length - 1).map(field => trigger(field as (keyof IFormData)[])));
      }
    }
  };

  const handleNext = async () => {
    const isSummaryStep = activeStep === steps.length - 2; // Check if the next step is the Summary step
    const newActiveStep = activeStep + 1;
    setActiveStep(newActiveStep);
    setVisitedSteps((prev) => {
      if ( !prev.includes(newActiveStep) ) return [...prev, newActiveStep];
      return prev;
    });
    if ( isSummaryStepVisited ){
      await trigger(stepFields[activeStep] as (keyof IFormData)[]);
    } else {
      if ( isSummaryStep ){
        if ( !isSummaryStepVisited ) setIsSummaryStepVisited(true);
        await Promise.all(stepFields.slice(0, steps.length - 1).map(field => trigger(field as (keyof IFormData)[])));
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const hasError = (fields:(keyof IFormData)[]): boolean => {
    return fields.some(field => !!errors[field]);
  };

  const getStepStatus = (step:number): { icon: ReactNode; sx:SxProps<Theme>; disabled:boolean; error:boolean } => {
    const active = activeStep === step;
    const disabled = !visitedSteps.includes(step);
    const error = hasError(stepFields[step] as (keyof IFormData)[]);
    return {
      icon: error
        ? <ErrorIcon color="error" />
        : active
          ? <RadioButtonCheckedIcon color="primary" />
          : <RadioButtonUncheckedIcon color="disabled" />
      ,
      sx: {
        cursor: 'pointer !important',
        '& .MuiStepLabel-iconContainer': {
          color: (theme:Theme) => `${theme.palette.primary.main} !important`
        },
        '& .MuiStepLabel-label': {
          color: (theme:Theme) => `${
            error
              ? theme.palette.error.main
              : active
                ? theme.palette.primary.main
                : theme.palette.grey[700]
          }`,
          '&.Mui-completed': {
            fontWeight: 400
          }
        }
      },
      error,
      disabled
    };
  };

  const handleClose = (isAccepted:boolean) => {
    toggleCheckDialog();
    dispatch(casesApi.util.resetApiState());

    if ( !isAccepted ) return;

    asyncCreateInsuranceCase();
  }

  const getDisplay = (step: number) => activeStep === step ? 'block' : 'none';

  return (
    <Fragment>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        title={t('pages.cases.createFormTitle')}
      >
        <FormProvider {...methods}>
          <Box
            sx={{ width: '100%' }}
            component="form"
            onSubmit={onSubmit}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 4 }}>
                <Stepper activeStep={activeStep} orientation="vertical">
                  {steps.map((label:string, index:number) => {
                    const { icon, sx, disabled, error } = getStepStatus(index);
                    return (
                      <Step
                        key={label}
                        disabled={disabled}
                        onClick={handleStepClick(index)}
                      ><StepLabel sx={sx} icon={icon} error={error}>{label}</StepLabel></Step>
                    );
                  })}
                </Stepper>
              </Grid>
              <Grid size={{ xs: 8 }}>
                {activeStep < steps.length ? (
                  <Box sx={{ minHeight: 540 }}>
                    <Box sx={{ display: getDisplay(0) }}><GeneralStepContent /></Box>
                    <Box sx={{ display: getDisplay(1) }}><ClaimantStepContent /></Box>
                    <Box sx={{ display: getDisplay(2) }}><ContactsStepContent /></Box>
                    <Box sx={{ display: getDisplay(3) }}><DetailsStepContent /></Box>
                    {hcpcsCodesEnabled ? (
                      <Box sx={{ display: getDisplay(4) }}><MedicareStepContent /></Box>
                    ) : null}
                    <Box sx={{ display: getDisplay(steps.length - 2) }}><NotificationsStepContent /></Box>
                    <Box sx={{ display: getDisplay(steps.length - 1) }}><ReviewCreateStepContent /></Box>
                  </Box>
                ) : null}
              </Grid>
            </Grid>
            {activeStep < steps.length ? (
              <Box sx={{
                mt: 4,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2
              }}>
                <Button
                  name="Back"
                  disabled={activeStep === 0 || (isLoading || loading)}
                  onClick={handleBack}
                >{t('buttons.back')}</Button>
                {activeStep === steps.length - 1 ? (
                  <Button
                    name={`Create case form dialog`}
                    loading={isLoading || loading}
                    disabled={Boolean(Object.keys(errors).length)}
                    type="submit"
                    variant="contained"
                    color="primary"
                  >{t('buttons.create')}</Button>
                ) : (
                  <Button
                    name={`Next ${activeStep}`}
                    onClick={handleNext}
                    color="primary"
                    variant="contained"
                  >{t('buttons.next')}</Button>
                )}
              </Box>
            ) : null}
          </Box>
        </FormProvider>
      </Dialog>
      {openCheckDialog ? (
        <CaseCheckDialog
          open={open}
          onClose={handleClose}
          searchCasesData={searchCasesData}
        />
      ) : null}
    </Fragment>
  );
};

export default CaseCreateFormDialog;
